/**
 * Texto en español para el kit: comparar y buscar como lo espera una persona (y como lo hace el
 * API con `collation { locale: 'es', strength: 1 }`): sin distinguir mayúsculas ni tildes, con la ñ
 * como letra propia y los valores ausentes primero.
 */

const collator = new Intl.Collator('es', { sensitivity: 'base' });

/** Tilde de la ñ (U+0303) detrás de una n, y las marcas combinantes que se quitan. */
const ENYE = /([nN])\u0303/g;
const MARKS = /[\u0300-\u036F]/g;
const SPACES = /\s+/g;

/** Marca provisoria (uso privado de Unicode) para la tilde de la ñ mientras se quitan las demás. */
const ENYE_MARK = '\uE000';
const ENYE_MARK_RE = /\uE000/g;

const isMissing = (value: unknown) => value === null || value === undefined;

/** Minúsculas, sin tildes (salvo la de la ñ) y con los espacios juntos; ausente → ''. */
export function normalizeSearch(value: unknown): string {
  if (isMissing(value)) return '';
  return String(value)
    .replace(ENYE_MARK_RE, '')
    .normalize('NFD')
    .replace(ENYE, `$1${ENYE_MARK}`)
    .replace(MARKS, '')
    .replace(ENYE_MARK_RE, '\u0303')
    .normalize('NFC')
    .toLocaleLowerCase('es')
    .replace(SPACES, ' ')
    .trim();
}

/** «Contiene», sin distinguir mayúsculas ni tildes. Una búsqueda vacía encuentra todo. */
export function matchesSearch(haystack: unknown, needle: string): boolean {
  const wanted = normalizeSearch(needle);
  if (!wanted) return true;
  if (isMissing(haystack) || haystack === '') return false;
  return normalizeSearch(haystack).includes(wanted);
}

/**
 * Orden por tipo, como el de Mongo (BSON): ausente (null/undefined) < número (NaN primero) <
 * texto < booleano < fecha. Así es un orden total aunque una columna mezcle tipos.
 */
function typeRank(value: unknown): number {
  if (isMissing(value)) return 0;
  if (typeof value === 'number') return 1;
  if (typeof value === 'string') return 2;
  if (typeof value === 'boolean') return 4;
  if (value instanceof Date) return 5;
  return 3;
}

/** Comparador para ordenar: por tipo como Mongo; dentro del tipo, por valor (texto en español). */
export function compareEs(a: unknown, b: unknown): number {
  const rank = typeRank(a) - typeRank(b);
  if (rank !== 0) return rank;
  if (isMissing(a)) return 0;
  if (typeof a === 'number' && typeof b === 'number') {
    if (Number.isNaN(a) || Number.isNaN(b))
      return Number(!Number.isNaN(a)) - Number(!Number.isNaN(b));
    return a - b;
  }
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b);
  if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();
  return collator.compare(String(a), String(b));
}
